import { PartFinderReport } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { useClearPartFinderReportRecommendation } from '@/hooks/api/part-finder-reports/useClearPartFinderReportRecommendation';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useUpdatePartFinderReportRecommendation } from '@/hooks/api/part-finder-reports/useUpdatePartFinderReportRecommendation';
import { getApiError } from '@/types/apiError';
import {
  AutoAwesomeOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  RateReviewOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Skeleton,
  TextField,
  Tooltip,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { CollapsibleSection } from './CollapsibleSection';
import { FadeIn } from '@/components/PartFinder/FadeIn';
import { PartFinderFeedbackControl } from './PartFinderFeedbackControl';

const Stack = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

const InlineActions = styled(InputAdornment)({
  alignSelf: 'flex-end',
  marginBottom: '6px',
  marginRight: '4px',
  gap: '2px',
});

const AiSuggestionBox = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.secondary.main, 0.06),
  border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
  borderRadius: '6px',
  padding: '10px 12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
}));

const AiHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
});

const AiLabel = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  color: theme.palette.secondary.main,
  fontWeight: 600,
  fontSize: '0.8125rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
}));

const SavedDisplay = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: '14px 44px 14px 16px',
  borderLeft: `3px solid ${theme.palette.secondary.main}`,
  backgroundColor: alpha(theme.palette.secondary.main, 0.04),
  borderRadius: '0 6px 6px 0',
  '& .edit-affordance': {
    opacity: 0,
    transition: theme.transitions.create('opacity', { duration: theme.transitions.duration.shortest }),
  },
  '&:hover .edit-affordance, &:focus-within .edit-affordance': {
    opacity: 1,
  },
}));

const EditAffordance = styled(Box)({
  position: 'absolute',
  top: '8px',
  right: '8px',
});

const PLACEHOLDER = 'Add your conclusion — supplier, lead time, anything a reviewer needs to know.';
const MAX_FINAL_RECOMMENDATION_LENGTH = 8000;

const AiSuggestionSkeleton = () => (
  <AiSuggestionBox>
    <AiHeader>
      <AiLabel>
        <AutoAwesomeOutlined sx={{ fontSize: 16 }} />
        AI Suggestion
      </AiLabel>
    </AiHeader>
    <Box>
      <Skeleton variant='text' width='96%' height={14} />
      <Skeleton variant='text' width='88%' height={14} />
      <Skeleton variant='text' width='72%' height={14} />
    </Box>
  </AiSuggestionBox>
);

export const FinalRecommendationSection = ({
  report,
  isGenerating,
}: {
  report: PartFinderReport;
  isGenerating: boolean;
}) => {
  const savedValue = report.finalRecommendation ?? '';
  const hasSavedValue = savedValue.length > 0;
  const hasActionableContent =
    hasSavedValue ||
    Boolean(report.aiSuggestedRecommendation) ||
    (report.sourcingOpportunities !== undefined && report.sourcingOpportunities.length > 0);

  const { permissions } = useGetCurrentUser();
  const canEdit = permissions?.canEditPartFinderReport ?? false;

  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(savedValue);
  const [expanded, setExpanded] = useState(hasActionableContent);
  const hasAutoExpandedReference = useRef(hasActionableContent);
  const setMutation = useUpdatePartFinderReportRecommendation(report.id);
  const clearMutation = useClearPartFinderReportRecommendation(report.id);

  const isPending = setMutation.isPending || clearMutation.isPending;
  const activeError = setMutation.error ?? clearMutation.error;

  const resetMutations = () => {
    setMutation.reset();
    clearMutation.reset();
  };

  useEffect(() => {
    if (isEditing) return;
    setValue(savedValue);
  }, [savedValue, isEditing]);

  useEffect(() => {
    if (!hasAutoExpandedReference.current && hasActionableContent) {
      hasAutoExpandedReference.current = true;
      setExpanded(true);
    }
  }, [hasActionableContent]);

  if (!canEdit && !hasSavedValue) {
    return;
  }

  const trimmed = value.trim();
  const isDirty = trimmed !== savedValue.trim();
  const apiError = activeError ? getApiError(activeError, 'Failed to save final recommendation') : undefined;

  const handleSave = () => {
    if (!isDirty) return;
    resetMutations();
    const onSuccess = () => {
      setValue(trimmed);
      setIsEditing(false);
    };
    if (trimmed === '') {
      clearMutation.mutate(undefined, { onSuccess });
    } else {
      setMutation.mutate({ finalRecommendation: trimmed }, { onSuccess });
    }
  };

  const handleCancelEdit = () => {
    resetMutations();
    setValue(savedValue);
    setIsEditing(false);
  };

  const handleStartEdit = () => {
    resetMutations();
    setIsEditing(true);
  };

  const handleUseAiSuggestion = () => {
    if (!report.aiSuggestedRecommendation) return;
    resetMutations();
    setValue(report.aiSuggestedRecommendation);
  };

  const showEditor = canEdit && (!hasSavedValue || isEditing);
  const showSavedDisplay = !showEditor && hasSavedValue;
  const showAiSkeleton = !hasSavedValue && isGenerating && !report.aiSuggestedRecommendation;
  const showAiSuggestion = !hasSavedValue && Boolean(report.aiSuggestedRecommendation);

  return (
    <CollapsibleSection
      icon={<RateReviewOutlined />}
      title='Final Recommendation'
      tooltip='Your conclusion on this report. Saved with the report so it persists across sessions.'
      expanded={expanded}
      onExpandedChange={setExpanded}
    >
      <Stack>
        {showEditor && (
          <TextField
            value={value}
            onChange={event => setValue(event.target.value)}
            placeholder={PLACEHOLDER}
            multiline
            minRows={2}
            maxRows={10}
            fullWidth
            disabled={isPending}
            autoFocus={isEditing}
            inputProps={{ 'aria-label': 'Final recommendation', maxLength: MAX_FINAL_RECOMMENDATION_LENGTH }}
            slotProps={{
              input: {
                endAdornment: (
                  <InlineActions position='end'>
                    {isEditing && (
                      <Tooltip title='Cancel edit'>
                        <span>
                          <IconButton
                            size='small'
                            onClick={handleCancelEdit}
                            disabled={isPending}
                            aria-label='Cancel edit'
                          >
                            <CloseOutlined fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {(isDirty || isPending) && (
                      <Tooltip title='Save recommendation'>
                        <span>
                          <IconButton
                            size='small'
                            color='primary'
                            onClick={handleSave}
                            disabled={isPending}
                            aria-label='Save final recommendation'
                          >
                            {isPending ? <CircularProgress size={16} /> : <CheckOutlined fontSize='small' />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </InlineActions>
                ),
              },
            }}
          />
        )}
        {showSavedDisplay && (
          <SavedDisplay>
            <BodyText sx={{ whiteSpace: 'pre-wrap' }}>{savedValue}</BodyText>
            {canEdit && (
              <EditAffordance className='edit-affordance'>
                <Tooltip title='Edit recommendation'>
                  <IconButton size='small' onClick={handleStartEdit} aria-label='Edit final recommendation'>
                    <EditOutlined fontSize='small' />
                  </IconButton>
                </Tooltip>
              </EditAffordance>
            )}
          </SavedDisplay>
        )}
        {apiError && <Alert severity='error'>{apiError.message}</Alert>}
        {showAiSkeleton && (
          <FadeIn key='ai-suggestion-skeleton'>
            <AiSuggestionSkeleton />
          </FadeIn>
        )}
        {showAiSuggestion && (
          <FadeIn key='ai-suggestion-loaded'>
            <AiSuggestionBox>
              <AiHeader>
                <AiLabel>
                  <AutoAwesomeOutlined sx={{ fontSize: 16 }} />
                  AI Suggestion
                </AiLabel>
                {canEdit && (
                  <Button
                    size='small'
                    onClick={handleUseAiSuggestion}
                    disabled={isPending || value === report.aiSuggestedRecommendation}
                  >
                    Use this
                  </Button>
                )}
              </AiHeader>
              <BodyText color='text.secondary'>{report.aiSuggestedRecommendation}</BodyText>
              <PartFinderFeedbackControl reportId={report.id} targetId={report.id} promptText='Was this helpful?' />
            </AiSuggestionBox>
          </FadeIn>
        )}
      </Stack>
    </CollapsibleSection>
  );
};
